<script lang="ts" setup>
import { ref } from 'vue';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores/browserStore';

import { AppButton, AppModal } from '@libs/ui';
import type { Location } from '@libs/maps';
import type { FlightAutoCompleteSuggestion } from '@libs/trip-planner-interfaces';

import type {
  AddFligthsModalRoute,
  AddFlightsModalInitialRoute,
} from './addFlightsModal.model';
import { useAddFlighstModalStore } from './store/addFlightsModalStore';
import AddFlightsViewNew from './AddFlightsViewNew.vue';
import AddFlightsHomeAirportCheckbox from './AddFlightsHomeAirportCheckbox.vue';

interface AddFlightsModalNewProps {
  routes: AddFlightsModalInitialRoute[];
  startPositon: Location | undefined;
  endPosition: Location | undefined;
}

const props = defineProps<AddFlightsModalNewProps>();

interface AddFlightsModalNewEmits {
  (eventName: 'close', context: void): void;
  (eventName: 'confirm', routes: AddFligthsModalRoute[]): void;
}

const emit = defineEmits<AddFlightsModalNewEmits>();

const addFlightsModalStore = useAddFlighstModalStore();
const browserStore = useBrowserStore();

const modalSubtitle = ref<string | undefined>(undefined);
const tempAirportAutosuggestion = ref<FlightAutoCompleteSuggestion | undefined>(
  undefined
);

function handleClose(): void {
  if (addFlightsModalStore.isEditing) {
    closeAirportSelection();
    return;
  }
  emit('close');
}

function handleConfirm(): void {
  if (addFlightsModalStore.isEditing) {
    updateAirport();
    closeAirportSelection();
    return;
  }
  emit('confirm', addFlightsModalStore.routes);
}

function closeAirportSelection(): void {
  addFlightsModalStore.clearEditingAirport();
  modalSubtitle.value = undefined;
}

function onToggleChange(): void {
  addFlightsModalStore.setShouldUpdateHomeAirport();
}

function storeAirportDataToBeUpdated(
  airportSuggestion: FlightAutoCompleteSuggestion | undefined
): void {
  tempAirportAutosuggestion.value = airportSuggestion;
}

function updateAirport(): void {
  if (tempAirportAutosuggestion.value) {
    addFlightsModalStore.updateAirport(tempAirportAutosuggestion.value);
    tempAirportAutosuggestion.value = undefined;
  }
}
</script>

<template>
  <AppModal class="add-flights-modal" @close="handleClose">
    <section
      :class="[
        'add-flights-modal-wrapper',
        {
          editing:
            addFlightsModalStore.isEditing && browserStore.isSmallerThan900px,
        },
      ]"
    >
      <header
        v-if="addFlightsModalStore.isEditing && browserStore.isSmallerThan900px"
        class="header"
      >
        <div class="title">
          {{ $t('modals.add_flights_new.search_airport') }}
        </div>
        <AppButton
          type="secondary"
          state="enabled"
          size="medium"
          :icon="{ name: 'close' }"
          @click="handleClose"
        />
      </header>
      <main class="top-section">
        <div v-if="browserStore.isLargerThan899px" class="photo-section">
          <div class="title">
            {{ $t('modals.add_flights_new.go_your_own_way') }}
          </div>
          <div class="description">
            {{ $t('modals.add_flights_new.create_your_dream_trip') }}
          </div>
          <div class="photo" />
        </div>
        <AddFlightsViewNew
          class="flights-view"
          :routes="props.routes"
          :start-positon="props.startPositon"
          :end-position="props.endPosition"
          @airport-data-to-be-updated="storeAirportDataToBeUpdated"
        >
          <AddFlightsHomeAirportCheckbox
            v-if="
              browserStore.isSmallerThan900px && !addFlightsModalStore.isEditing
            "
            :checked="addFlightsModalStore.shouldUpdateHomeAirport"
            @toggle="onToggleChange"
          />
        </AddFlightsViewNew>
      </main>
      <footer class="footer">
        <div
          :class="[
            'footer-actions',
            { 'only-buttons': browserStore.isSmallerThan900px },
          ]"
        >
          <AddFlightsHomeAirportCheckbox
            v-if="browserStore.isLargerThan899px"
            :checked="addFlightsModalStore.shouldUpdateHomeAirport"
            @toggle="onToggleChange"
          />
          <div
            v-if="
              addFlightsModalStore.isEditing && browserStore.isSmallerThan900px
            "
            class="buttons wide"
          >
            <AppButton
              state="enabled"
              class="button"
              type="primary"
              :wide="true"
              :size="browserStore.isLargerThan899px ? 'large' : 'medium'"
              @click="handleConfirm"
            >
              {{ $t('planner.done') }}
            </AppButton>
          </div>
          <div v-else class="buttons">
            <AppButton
              state="enabled"
              class="button"
              type="secondary"
              :wide="true"
              :size="browserStore.isLargerThan899px ? 'large' : 'medium'"
              @click="handleClose"
            >
              {{ $t('planner.close') }}
            </AppButton>
            <AppButton
              :state="
                addFlightsModalStore.isButtonDisabled ? 'disabled' : 'enabled'
              "
              class="button"
              type="primary"
              :wide="true"
              :size="browserStore.isLargerThan899px ? 'large' : 'medium'"
              @click="handleConfirm"
            >
              {{ $t('modals.addFlights.buttons.addFlights') }}
            </AppButton>
          </div>
        </div>
      </footer>
    </section>
  </AppModal>
</template>

<style lang="scss" scoped>
.add-flights-modal-wrapper {
  --footer-height: 84px;

  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  padding-top: var(--spacing-32);
  padding-bottom: var(--spacing-24);

  @include for-less-or-equal-than-899-px {
    padding-top: var(--spacing-12);

    &.editing {
      padding-top: var(--spacing-24);

      .header {
        padding: 0 var(--spacing-12) var(--spacing-32);
      }
    }
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 var(--spacing-24) var(--spacing-32);

    .title {
      color: var(--color-neutral-70);
      @include font-label-xl-bold;
    }
  }

  .top-section {
    display: flex;
    width: 100%;
    height: calc(100% - var(--footer-height));
    gap: var(--spacing-24);
    padding-left: var(--spacing-24);
    padding-right: var(--spacing-24);

    @include for-less-or-equal-than-899-px {
      background-image: url('../../../assets/red_ribbon.png');
      background-repeat: no-repeat;
      background-position: right bottom;
    }

    @include for-less-or-equal-than-599-px {
      padding-left: var(--spacing-12);
      padding-right: var(--spacing-12);
    }

    .photo-section {
      width: 100%;
      background-color: var(--color-brand-lilac);
      border-radius: var(--border-radius-12);
      padding: var(--spacing-24);
      color: var(--color-black);
      flex: 2;
      display: flex;
      flex-direction: column;
      margin-bottom: var(--spacing-40);

      .title {
        @include font-heading-4;
        margin-bottom: var(--spacing-12);
      }

      .description {
        @include font-body-m-light;
        margin-bottom: var(--spacing-12);
      }

      .photo {
        border-radius: var(--border-radius-12);
        background-image: url('../../../assets/amelia_summer.png');
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        flex: 1;
      }
    }

    .flights-view {
      width: 100%;
      flex: 5;
      overflow-y: scroll;
      padding-bottom: var(--spacing-12);
    }
  }

  .footer {
    border-top: 1px solid var(--color-neutral-30);
    height: var(--footer-height);

    .footer-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      height: 100%;

      padding-top: var(--spacing-12);
      padding-left: var(--spacing-24);
      padding-right: var(--spacing-24);

      &.only-buttons {
        justify-content: center;
      }

      .checkbox {
        background-color: var(--color-neutral-10);
        border-radius: var(--border-radius-8);
        padding: var(--spacing-12);

        .checkbox-label {
          display: flex;
          flex-direction: column;

          .title {
            @include font-body-l-bold;
          }

          .description {
            @include font-body-l-light;
          }
        }
      }

      .buttons {
        display: flex;
        align-items: center;
        gap: var(--spacing-24);
        width: 324px;

        &.wide {
          width: 100%;
        }

        @include for-less-or-equal-than-899-px {
          gap: var(--spacing-16);
          width: 336px;
        }
      }
    }
  }

  @include for-less-or-equal-than-899-px {
    padding-bottom: 0;
    gap: 0;

    .top-section {
      flex-direction: column;
    }

    .footer {
      .footer-actions {
        height: auto;

        padding-top: var(--spacing-16);
        padding-bottom: var(--spacing-24);
      }
    }
  }
}
</style>
